package ai.wagemix.web.screens.jobdescriptions

import ai.wagemix.net.models.WMUploadFile
import ai.wagemix.net.models.WMUploadProgress
import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.jobdescriptions.WMDefaultJobDescriptionsPresenter
import ai.wagemix.logic.jobdescriptions.WMJobDescriptionsPresenter
import ai.wagemix.logic.jobdescriptions.WMJobDescriptionsPresenter.Action
import ai.wagemix.logic.jobdescriptions.WMJobDescriptionsPresenter.Event
import ai.wagemix.logic.jobdescriptions.WMJobDescriptionsPresenter.Prompt
import ai.wagemix.logic.jobdescriptions.WMUploadQuota
import ai.wagemix.net.models.WMUpload
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import com.fsryan.foundation.web.screens.prompting.FSWebPromptingViewModel
import com.fsryan.foundation.web.screens.prompting.FSWebPromptingViewStateHolder

interface WMJobDescriptionsWebViewStateHolder<out P: Prompt>: FSWebPromptingViewStateHolder<P> {
    val uploadsState: State<List<WMUpload>>
    val quotaState: State<WMUploadQuota?>
    val activeUploadState: State<WMUploadProgress?>
    /** The subscription ended: the page shows the renewal state instead of the uploads (paid-feature plan W7). */
    val lapsedState: State<Boolean>
}

abstract class WMBaseJobDescriptionsWebViewModel<
    P: Prompt,
    E: Event<P>,
    A: Action,
    PR: WMJobDescriptionsPresenter<P, E, A>
>(
    presenter: PR,
    protected val externalNavigation: (WMNavigationRequest) -> Unit
): FSWebPromptingViewModel<P, E, A, PR>(presenter), WMJobDescriptionsWebViewStateHolder<P> {

    override val uploadsState: State<List<WMUpload>> get() = _uploadsState
    protected val _uploadsState = mutableStateOf<List<WMUpload>>(emptyList())

    override val quotaState: State<WMUploadQuota?> get() = _quotaState
    protected val _quotaState = mutableStateOf<WMUploadQuota?>(null)

    override val activeUploadState: State<WMUploadProgress?> get() = _activeUploadState
    protected val _activeUploadState = mutableStateOf<WMUploadProgress?>(null)

    override val lapsedState: State<Boolean> get() = _lapsedState
    protected val _lapsedState = mutableStateOf(false)

    override fun customEventHandling(event: E) {
        super.customEventHandling(event)
        with(event) {
            uploadsUpdate?.let { _uploadsState.value = it }
            quotaUpdate?.let { _quotaState.value = it }
            activeUploadUpdate?.let { _activeUploadState.value = it.value }
            lapsedUpdate?.let { _lapsedState.value = it }
            navigationUpdate?.let { externalNavigation(it) }
        }
    }

    fun userDroppedFile(file: WMUploadFile) = presenter.userDroppedFile(file)
    fun userSubmittedUrl(url: String) = presenter.userSubmittedUrl(url)
    fun userRequestedOpenMix(mixId: String) = presenter.userRequestedOpenMix(mixId)
    fun userRequestedFilePreview(mixId: String) = presenter.userRequestedFilePreview(mixId)
    fun userRequestedReevaluate(mixId: String) = presenter.userRequestedReevaluate(mixId)
    fun userRequestedRetryUpload(uploadId: String) = presenter.userRequestedRetryUpload(uploadId)
    fun userRequestedDeleteUpload(uploadId: String) = presenter.userRequestedDeleteUpload(uploadId)
}

typealias WMDefaultJobDescriptionsWebViewModel = WMBaseJobDescriptionsWebViewModel<Prompt.Base, Event.Base<Prompt.Base>, Action.Base, WMDefaultJobDescriptionsPresenter>

fun createDefaultJobDescriptionsWebViewModel(
    presenter: WMDefaultJobDescriptionsPresenter,
    externalNavigation: (WMNavigationRequest) -> Unit
): WMDefaultJobDescriptionsWebViewModel = object: WMDefaultJobDescriptionsWebViewModel(presenter, externalNavigation) {
    override val defaultPrompt: Prompt.Base
        get() = Prompt.baseFactory().none()
}
